import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { observer } from "mobx-react";
import { CartAbandonmentOptOut } from "./components/CartAbandonmentOptOut.js";
import { CheckoutSkeleton } from "./components/CheckoutSkeleton.js";
import { ClaimCashback } from "./components/ClaimCashback.js";
import { FatalError } from "./components/FatalError.js";
import { HeroSection } from "./components/hero/HeroSection.js";
import { TwoColumnLayout } from "./components/layout/TwoColumnLayout.js";
import { IdentificationForm } from "./components/left/IdentificationForm.js";
import { PaymentSection } from "./components/left/PaymentSection.js";
import { KeyBenefits } from "./components/right/KeyBenefits.js";
import { MoneyBackGuarantee } from "./components/right/MoneyBackGuarantee.js";
import { OrderSummary } from "./components/right/OrderSummary.js";
import { SuccessView } from "./components/success/SuccessView.js";
import { useAbandonGuard } from "./hooks/useAbandonGuard.js";
import { useCheckoutV2StepInvokeGtag } from "./hooks/useCheckoutV2StepInvokeGtag.js";
import { useCheckoutV2StepInvokeMatomoPageView } from "./hooks/useCheckoutV2StepInvokeMatomoPageView.js";
import { useCheckoutV2Transaction } from "./hooks/useCheckoutV2Transaction.js";
import { useDelayedReveal } from "./hooks/useDelayedReveal.js";
import { useHtmlTitle } from "./hooks/useHtmlTitle.js";
import { useStores } from "../../store/stores.js";
import { __ } from "../../utils/i18n.js";
/**
 * Main two-column checkout. Left column is step-switched: pre-submit shows
 * the identification form + payment-methods preview, post-submit unmounts
 * the form and Paddle's iframe takes its place. Right column holds the
 * Order summary + Money-Back; Benefits go in `trustSignals` (desktop-only).
 */ const CheckoutView = observer(()=>{
    const { checkoutV2Store } = useStores();
    const isSubmitted = !!checkoutV2Store.transaction?.identification;
    const productName = checkoutV2Store.product?.data.productPlan.product.name;
    // Paid human services (e.g. Cookie-Expert setup) can't honour a refund
    // promise once the work has been performed, so the guarantee callout is
    // suppressed for them. One-time plugin licenses like Real Media Library
    // are not affected — `isPaidService` reads the plan's legal-ack teachings,
    // not the payment interval.
    const showMoneyBack = checkoutV2Store.product ? !checkoutV2Store.product.isPaidService : false;
    return /*#__PURE__*/ _jsxs("main", {
        "aria-label": productName ? __("Checkout for %s", productName) : __("Checkout"),
        children: [
            productName && /*#__PURE__*/ _jsx("h1", {
                className: "checkout-v2-sr-only",
                children: __("Checkout for %s", productName)
            }),
            /*#__PURE__*/ _jsx(HeroSection, {}),
            /*#__PURE__*/ _jsx(TwoColumnLayout, {
                left: isSubmitted ? /*#__PURE__*/ _jsx(PaymentSection, {}) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(IdentificationForm, {}),
                        /*#__PURE__*/ _jsx(PaymentSection, {})
                    ]
                }),
                right: /*#__PURE__*/ _jsxs("div", {
                    style: {
                        width: "100%"
                    },
                    children: [
                        /*#__PURE__*/ _jsx(OrderSummary, {}),
                        showMoneyBack && /*#__PURE__*/ _jsx("div", {
                            style: {
                                marginTop: 24
                            },
                            children: /*#__PURE__*/ _jsx(MoneyBackGuarantee, {})
                        })
                    ]
                }),
                trustSignals: // Benefits collapse (soft-exit) on Payment step — Money-Back answers the
                // "am I making the right choice?" worry more directly there.
                /*#__PURE__*/ _jsx("div", {
                    className: `checkout-v2-soft-exit${isSubmitted ? " checkout-v2-soft-exit--hidden" : ""}`,
                    "aria-hidden": isSubmitted,
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-soft-exit__inner",
                        style: {
                            paddingTop: 24
                        },
                        children: /*#__PURE__*/ _jsx(KeyBenefits, {})
                    })
                })
            })
        ]
    });
});
const LOADING_SKELETON_REVEAL_MS = 300;
const CheckoutV2Root = observer((props)=>{
    // Cart-abandonment opt-out short-circuits before the standard checkout bootstrap so that
    // we do not load product/plan data (or block tab-close via `useAbandonGuard`) for a buyer
    // who is here only to unsubscribe.
    const cartAbandonmentOptOutUuid = typeof window !== "undefined" ? new URLSearchParams(window.location.search).get("cae-opt-out") : null;
    const { loading, store, cashbackToken } = useCheckoutV2Transaction(props);
    // Keep the skeleton suppressed for the first few hundred milliseconds so
    // fast networks never flash a placeholder between navigation and final content.
    const revealSkeleton = useDelayedReveal(LOADING_SKELETON_REVEAL_MS);
    useHtmlTitle(store, store.product?.data.productPlan.product.name ?? "");
    // Block accidental tab-close / back-nav as soon as the buyer has typed
    // anything into the identification form and while the purchase isn't
    // yet finalised. Skipped on the cashback-claim and opt-out flows (no
    // purchase state to lose) and on the success view (the license is
    // already delivered).
    useAbandonGuard(!cashbackToken && !cartAbandonmentOptOutUuid && store.hasCheckoutFormInteraction && store.view !== "success");
    // Per-step Matomo virtual page-view (Personal information → Payment →
    // Confirmation) and the matching ecommerce-event funnel. Order is
    // load-bearing: the page-view hook fires `setCustomUrl` first so the
    // step event from the gtag hook lands in the correct virtual-URL
    // bucket — Matomo associates events with the most recent `setCustomUrl`.
    useCheckoutV2StepInvokeMatomoPageView();
    useCheckoutV2StepInvokeGtag();
    if (cartAbandonmentOptOutUuid) {
        return /*#__PURE__*/ _jsx(CartAbandonmentOptOut, {
            uuid: cartAbandonmentOptOutUuid
        });
    }
    if (cashbackToken) {
        return /*#__PURE__*/ _jsx(ClaimCashback, {
            token: cashbackToken
        });
    }
    if (store.fatalError) {
        return /*#__PURE__*/ _jsx(FatalError, {
            error: store.fatalError
        });
    }
    if (loading) {
        return revealSkeleton ? /*#__PURE__*/ _jsx(CheckoutSkeleton, {}) : null;
    }
    return store.view === "checkout" ? /*#__PURE__*/ _jsx(CheckoutView, {}) : /*#__PURE__*/ _jsx(SuccessView, {});
});
export { CheckoutV2Root };
